import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenProceduresIndicatorC3Data } from "@/services/esus/indicator/esf/c3/getDetailedCitizensProceduresIndicatorC3";
import { getCitizenScoreStatusDescriptionIndicatorC3 } from "@/utils/indicatorsEsf";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC3CitizensProceduresTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	dumDate: Column;
	gestationalAge: Column;
	dppDate: Column;
	firstTrimesterProceduresStatus: Column;
	thirdTrimesterProceduresStatus: Column;
};

type IndicatorEsfC3CitizensProceduresTableProps = {
	citizensProcedures: DetailedCitizenProceduresIndicatorC3Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithHomeVisits: DetailedCitizenProceduresIndicatorC3Data) => void;
};

export function IndicatorEsfC3CitizensProceduresTable({
	citizensProcedures,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC3CitizensProceduresTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC3CitizensProceduresTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		dumDate: { title: "DUM", visible: true },
		gestationalAge: { title: "IG", visible: true },
		dppDate: { title: "DPP", visible: true },
		firstTrimesterProceduresStatus: {
			title: "Status Exames/Proced. 1º Trimestre",
			visible: true,
		},
		thirdTrimesterProceduresStatus: {
			title: "Status Exames/Proced. 3º Trimestre",
			visible: true,
		},
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.dumDate.visible && "d-none"}`}>
						{columns.dumDate.title}
					</th>
					<th className={`px-2 ${!columns.dppDate.visible && "d-none"}`}>
						{columns.dppDate.title}
					</th>
					<th className={`px-2 ${!columns.gestationalAge.visible && "d-none"}`}>
						{columns.gestationalAge.title}
					</th>
					<th
						className={`px-2 ${
							!columns.firstTrimesterProceduresStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.firstTrimesterProceduresStatus.title}
							<CardOverlayTrigger
								header="Exames e procedimentos 1º trimestre"
								message="Realização de exames/procedimentos de hiv, sífilis, hepatite b e hepatite c no 1º trimestre do período gestacional (anterior a 13 semanas completas de gestação)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.thirdTrimesterProceduresStatus.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.thirdTrimesterProceduresStatus.title}
							<CardOverlayTrigger
								header="Exames e procedimentos 3º trimestre"
								message="Realização de exames/procedimentos de hiv e sífilis no 3º trimestre do período gestacional (28 a 42 semanas completas de gestação)"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC3CitizensProceduresTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC3CitizensProceduresTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC3CitizensProceduresTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensProcedures.length > 0
					? citizensProcedures.map((citizenWithProcedures, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.fullName}</div>
										<div className="text-muted">
											{citizenWithProcedures.birthdate
												? dayjs(citizenWithProcedures.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithProcedures.birthdate)}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.cpf
										? formatIdentifier(citizenWithProcedures.cpf)
										: citizenWithProcedures.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithProcedures.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dumDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithProcedures.dumDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.dppDate.visible && "d-none"
									}`}
								>
									{dayjs(citizenWithProcedures.dppDate).format("DD/MM/YYYY")}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.gestationalAge.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.gestationalAge ?? ""}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.firstTrimesterProceduresStatus.visible && "d-none"
									}`}
								>
									{getCitizenScoreStatusDescriptionIndicatorC3(
										citizenWithProcedures.firstTrimesterProceduresStatus
									)}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.thirdTrimesterProceduresStatus.visible && "d-none"
									}`}
								>
									{getCitizenScoreStatusDescriptionIndicatorC3(
										citizenWithProcedures.thirdTrimesterProceduresStatus
									)}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithProcedures)}
										title="Histórico de exames/procedimentos das gestantes"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
